import { FC, useState, useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSignupWizard } from './signup-wizard-context';
import WizardStep from './wizard-step';
import { useStepNavigation } from './use-step-navigation';
import { isValidEmail, uniqueID } from '../../util';
import { logEvent } from '../../services/eventsService';
import Field from '../../components/shared/Field/Field';
import Selector from '../../components/shared/Selector/Selector';
import Badge from '../../components/shared/Badge/Badge';
import BtButton from '../bt-button/bt-button';
import TooltipContainer from '../../components/shared/TooltipContainer/TooltipContainer';
import InfoBox from '../../components/shared/InfoBox/InfoBox';
import { ReactComponent as InfoIconLight } from '../../assets/svg/InfoIconLight.svg';
import { MultiValue } from 'react-select';

interface AddedColleague {
  id: string;
  name: string;
  email: string;
  title: string;
  phone: string;
  isAdmin: boolean;
  isContact: boolean;
}

type RoleOption = { label: string; value: string };

const StepInvite: FC = () => {
  const { t } = useTranslation('newSupplier', { keyPrefix: 'wizard' });
  const history = useHistory();
  const { state, updateState, completeStep, setCurrentStep } = useSignupWizard();

  const ROLE_OPTIONS: RoleOption[] = [
    { label: t('invite.roleUser', 'Användare'), value: 'admin' },
    { label: t('invite.roleContact', 'Kontaktperson'), value: 'contact' },
  ];

  const [colleagues, setColleagues] = useState<AddedColleague[]>([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [title, setTitle] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<RoleOption[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const clearForm = () => {
    setName(''); setEmail(''); setPhone(''); setTitle('');
    setSelectedRoles([]); setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = t('invite.errors.nameRequired', 'Namn krävs');
    if (!email.trim()) {
      newErrors.email = t('invite.errors.emailRequired', 'E-post krävs');
    } else if (!isValidEmail(email.trim())) {
      newErrors.email = t('invite.errors.emailInvalid', 'Ange en giltig e-postadress');
    }
    if (selectedRoles.length === 0) newErrors.role = t('invite.errors.roleRequired', 'Välj minst en roll');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildColleagueFromForm = (): AddedColleague => {
    const trimmed = { name: name.trim(), email: email.trim(), title: title.trim(), phone: phone.trim() };
    return {
      id: uniqueID(),
      ...trimmed,
      isAdmin: selectedRoles.some(r => r.value === 'admin'),
      isContact: selectedRoles.some(r => r.value === 'contact'),
    };
  };

  const handleAdd = () => {
    if (!validateForm()) return;
    setColleagues(prev => [...prev, buildColleagueFromForm()]);
    clearForm();
  };

  const removeColleague = (id: string) => {
    setColleagues(prev => prev.filter(c => c.id !== id));
  };

  const { handleBack } = useStepNavigation({
    currentStep: 5,
    prevPath: '/supplier-sign-up/about-you',
  });

  const proceedToVerify = (colleagueList: AddedColleague[]) => {
    updateState({
      colleagues: colleagueList.map(({ name, email, title, phone, isAdmin, isContact }) => ({
        name, email, title, phoneNumber: phone, isAdmin, isContact,
      })),
    });
    completeStep(5);
    logEvent({
      event: 'signup_wizard_step_invite',
      metadata: { sessionId: state.sessionId },
      userIdentifier: state.sessionId,
      options: { unique: true },
    });
    setCurrentStep(6);
    history.push('/supplier-sign-up/verify');
  };

  const handleNext = () => {
    const allColleagues = [...colleagues];
    if (name.trim() || email.trim()) {
      if (!validateForm()) return;
      allColleagues.push(buildColleagueFromForm());
    }
    proceedToVerify(allColleagues);
  };

  const handleSkip = () => proceedToVerify([]);

  const selectorLabel = useMemo(() => (
    <span className="flex items-center gap-1">
      {t('invite.roleLabel', 'Roll')}
      <TooltipContainer
        renderReferenceComponent={(className, ref) => (
          <span ref={ref} className={className} style={{ display: 'inline-flex', cursor: 'help' }}>
            <InfoIconLight style={{ width: '14px', height: '14px' }} />
          </span>
        )}
      >
        <div style={{ fontSize: '0.75rem', maxWidth: '220px' }}>
          <p style={{ marginBottom: '8px' }}><strong>{t('invite.roleTooltip.adminTitle', 'Användare:')}</strong> {t('invite.roleTooltip.adminDesc', 'Kan hantera företagsprofilen och svara på förfrågningar.')}</p>
          <p><strong>{t('invite.roleTooltip.contactTitle', 'Kontaktperson:')}</strong> {t('invite.roleTooltip.contactDesc', 'Visas i er publika profil - köpare kan kontakta dem direkt.')}</p>
        </div>
      </TooltipContainer>
    </span>
  ), []);

  return (
    <WizardStep
      step={5}
      title={t('invite.title', 'Vill du bjuda in kollegor? (Valfritt)')}
      description={t('invite.description', 'Lägg till ditt team')}
      onNext={handleNext}
      onBack={handleBack}
      canProceed={true}
      footer={
        <div className="text-center">
          <button
            type="button"
            onClick={handleSkip}
            style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--gray-700)', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            {t('invite.skip', 'Hoppa över')}
          </button>
        </div>
      }
    >
      <InfoBox description={t('invite.infoBanner', 'Involvera dina kollegor för att hantera förfrågningar, uppdatera profilen eller synas som kontaktpersoner för köpare.')} />

      {colleagues.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {colleagues.map(c => (
            <Badge
              key={c.id}
              backgroundColor="var(--orange-50, #FFF7ED)"
              color="var(--orange-600, #EA580C)"
              style={{ fontSize: '0.875rem', padding: '4px 12px', borderRadius: '9999px', border: '1px solid var(--orange-500)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
              onClick={() => removeColleague(c.id)}
            >
              {c.name}
              <span style={{ fontSize: '1rem', lineHeight: 1 }}>&times;</span>
            </Badge>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-4">
        <Field
          id="colleague-name"
          value={name}
          onChange={(val) => { setName(val); if (errors.name) setErrors(prev => ({ ...prev, name: '' })); }}
          placeholder={t('invite.namePlaceholder', 'Förnamn Efternamn')}
          label={t('invite.name', 'Namn')}
          required
          errorMessage={errors.name}
        />

        <div className="flex gap-4">
          <div className="flex-1">
            <Field
              id="colleague-email"
              type="email"
              value={email}
              onChange={(val) => { setEmail(val); if (errors.email) setErrors(prev => ({ ...prev, email: '' })); }}
              placeholder={t('invite.emailPlaceholder', 'kollega@företag.se')}
              label={t('invite.email', 'E-post')}
              required
              errorMessage={errors.email}
            />
          </div>
          <div className="flex-1">
            <Field
              id="colleague-phone"
              type="tel"
              value={phone}
              onChange={(val) => setPhone(val)}
              placeholder="+46 70 000 00 00"
              label={t('invite.phone', 'Telefonnummer')}
            />
          </div>
        </div>

        <Field
          id="colleague-title"
          value={title}
          onChange={(val) => setTitle(val)}
          placeholder={t('invite.titlePlaceholder', 't.ex. Platschef')}
          label={t('invite.titleLabel', 'Titel')}
        />

        <Selector
          options={ROLE_OPTIONS}
          value={selectedRoles}
          onChange={useCallback((val: MultiValue<RoleOption>) => {
            setSelectedRoles(val as RoleOption[]);
            if (errors.role) setErrors(prev => ({ ...prev, role: '' }));
          }, [errors.role])}
          isMulti={true}
          required
          placeholder={t('invite.rolePlaceholder', 'Välj roll/behörighet...')}
          label={selectorLabel}
          errorMessage={errors.role}
        />

        <BtButton
          color="white"
          onClick={handleAdd}
          style={{ borderColor: 'var(--orange-500)', color: 'var(--orange-500)' }}
        >
          {t('invite.add', 'Lägg till')}
        </BtButton>
      </div>
    </WizardStep>
  );
};

export default StepInvite;
